import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranscriptMetadataService } from '../transcript-metadata/transcript-metadata.service';
import { ConnectorExportService } from '../connector-export/connector-export.service';
import { EtriveConnectService } from '../etrive-connect/etrive-connect.service';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { TranscriptMappingService } from '../transcript-data-mapper/transcript-data-mapper.service';
import {
  TranscriptExtractionData,
  Course,
  FieldWithBbox,
  RequiredFields
} from '../../models/transcriptExtractionData.model';
import { TranscriptStatus, TranscriptType } from '../../constants';

export interface TranscriptDataResult {
  data: TranscriptExtractionData | null;
  statusId: number;
}

@Injectable({
  providedIn: 'root'
})
export class ItpExtractionDataService {
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private connectorExportService = inject(ConnectorExportService);
  private etriveConnectService = inject(EtriveConnectService);
  private featureFlagService = inject(FeatureFlagService);
  private transcriptMappingService = inject(TranscriptMappingService);

  /**
   * Loads transcript data from metadata service
   * @param transcriptId The transcript identifier
   */
  async loadTranscriptData(transcriptId: string): Promise<TranscriptDataResult> {
    const response = await this.transcriptMetadataService.loadExtraction(transcriptId);

    if (!response) {
      throw new Error('No transcript data available');
    }

    const content = response.savedData || response.origData || response.content;

    if (!content || content === '{}') {
      throw new Error('No transcript data available');
    }
    const transcriptType = response.typeId ?? 0;
    const rawData = typeof content === 'string' ? JSON.parse(content) : content;
    const mappedData = this.transcriptMappingService.mapToTranscriptData(rawData, transcriptType);

    if (response.studentId) {
      mappedData.Student.StudentId = {
        value: response.studentId,
        bbox: null,
        confidence: null,
        page: null
      };
    }
    return {
      data: mappedData,
      statusId: response.statusId || TranscriptStatus.ReadyForExport
    };
  }

  /**
   * Saves transcript data to the metadata service
   * @param transcriptId The transcript identifier
   * @param transcriptData The transcript data to save
   */
  async saveTranscriptData(
    transcriptId: string,
    transcriptData: TranscriptExtractionData
  ): Promise<void> {
    const content = JSON.stringify(transcriptData);
    const firstName = transcriptData.Student?.FirstName?.value || '';
    const lastName = transcriptData.Student?.LastName?.value || '';
    const studentId = transcriptData.Student?.StudentId?.value || '';

    await this.transcriptMetadataService.saveExtraction({
      TransId: transcriptId,
      SavedData: content,
      FirstName: firstName,
      LastName: lastName,
      StudentId: studentId
    });
  }

  /**
   * Downloads transcript data as a JSON file
   * @param transcriptData The transcript data to download
   */
  downloadAsJson(transcriptData: TranscriptExtractionData): void {
    const jsonStr = JSON.stringify(transcriptData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${transcriptData.Student?.LastName?.value || 'unknown'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Determines which hidden columns should be auto-shown based on data or required fields
   * @param courses The course data to check for values
   * @param requiredCourseFields The list of required course field names
   * @param columns The current column configuration
   * @returns Array of field names that should be made visible
   */
  getAutoVisibleColumns(
    courses: Course[],
    requiredCourseFields: string[],
    columns: { field: string; visible: boolean }[]
  ): string[] {
    return columns
      .filter(col => {
        if (col.visible) return false;

        const isRequired = requiredCourseFields.includes(col.field.toLowerCase());
        const hasData = courses.some(course => {
          const field = course[col.field as keyof Course] as FieldWithBbox | undefined;
          return field?.value && String(field.value).trim() !== '';
        });

        return isRequired || hasData;
      })
      .map(col => col.field);
  }

  /**
   * Ensures all courses have the specified optional field backfilled with empty values
   * @param courses The course data to backfill
   * @param field The field name to ensure exists on all courses
   */
  ensureCourseField(courses: Course[], field: string): void {
    if (field === 'InstitutionId') {
      for (const course of courses) {
        if (!course.InstitutionId) {
          course.InstitutionId = { value: '', bbox: null, confidence: null, page: null };
        }
      }
    }
  }

  /**
   * Loads the fields required to export a transcript of the given type through its connector.
   * Fields no configuration can waive (e.g. last name, institution name) are already folded
   * in by the API, so nothing here needs to force them.
   * @param transcriptTypeId Type of transcript it is
   */
  async loadRequiredFields(transcriptTypeId: TranscriptType): Promise<RequiredFields> {
    const highSchoolTranscriptsEnabled = await firstValueFrom(
      this.featureFlagService.getFlag('feature-itp-high-school-transcripts', false)
    );

    if (!highSchoolTranscriptsEnabled) {
      return this.loadRequiredFieldsEtrieveConnect();
    }

    const requirements = await this.connectorExportService.getExportRequirements(transcriptTypeId);

    return {
      student: requirements?.requiredStudentFields.map(field => field.toLowerCase()) ?? [],
      course: requirements?.requiredCourseFields.map(field => field.toLowerCase()) ?? [],
      institution:
        requirements?.requiredInstitutionFields.map(field => {
          const lower = field.toLowerCase();
          return lower === 'name' ? 'institutionname' : lower;
        }) ?? []
    };
  }

  /**
   * Pre-connector-export required fields lookup, kept behind feature-itp-high-school-transcripts
   * until the new endpoint is rolled out.
   */
  private async loadRequiredFieldsEtrieveConnect(): Promise<RequiredFields> {
    const requiredFieldsResponse = await this.etriveConnectService.getRequiredFields();

    const requiredStudentFields =
      requiredFieldsResponse?.requiredStudentFields?.map((field: string) => field.toLowerCase()) ??
      [];
    const requiredCourseFields =
      requiredFieldsResponse?.requiredCourseFields?.map((field: string) => field.toLowerCase()) ??
      [];
    const requiredInstitutionFields =
      requiredFieldsResponse?.requiredInstitutionFields?.map((field: string) => {
        const lower = field.toLowerCase();
        return lower === 'name' ? 'institutionname' : lower;
      }) ?? [];

    // ensure 'lastname' is included for student
    if (!requiredStudentFields.includes('lastname')) {
      requiredStudentFields.push('lastname');
    }

    // ensure 'institutionname' is included for institution
    if (!requiredInstitutionFields.includes('institutionname')) {
      requiredInstitutionFields.push('institutionname');
    }

    return {
      student: requiredStudentFields,
      course: requiredCourseFields,
      institution: requiredInstitutionFields
    };
  }
}
